import {
  EditOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { KnowledgeBaseApi, currentUserIsAdminSelector } from '@hefe/store';
import {
  formatDateTime,
  fullUserName,
  useHtmlWithAuthImages,
} from '@hefe/utils';
import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  Result,
  Row,
  Skeleton,
  Space,
  Typography,
  Upload,
  message,
} from 'antd';
import { RcFile } from 'antd/es/upload';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import UserAvatar from '../../../core/components/user/user-avatar/user-avatar/user-avatar.component';
import HtmlParser from '../../../core/utils/html-parser/HtmlParser';
import KnowledgeBaseAttachmentComponent from '../../components/knowledge-base-attachment/knowledge-base-attachment.component';
import KnowledgeBaseLayout from '../../layout/knowledge-base.layout';

export default function KnowledgeBaseArticlePage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const userIsAdmin = useSelector(currentUserIsAdminSelector);

  const articleId = parseInt(id ?? 'a', 10);

  const ACCEPTED_FILE_TYPES = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'zip',
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
  ];

  const { data: article, isFetching: isFetchingArticle } =
    KnowledgeBaseApi.useFetchArticleQuery(articleId, {
      skip: isNaN(articleId),
    });

  const [uploadFile, { isLoading: isUploadingFile }] =
    KnowledgeBaseApi.useUploadFileMutation();

  const { data } = useHtmlWithAuthImages(article?.content ?? '');

  const beforeUpload = (file: RcFile) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const isAcceptedFileType = ACCEPTED_FILE_TYPES.includes(
      fileExtension ?? ''
    );

    if (!isAcceptedFileType) {
      message.error(
        t('knowledge_base.articles.detail_page.attachments.upload.error.format')
      );
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(
        t('knowledge_base.articles.detail_page.attachments.upload.error.size')
      );

      return false;
    }

    if (isAcceptedFileType && isLt2M) {
      uploadAttachment(file);
    }

    return false;
  };

  const uploadAttachment = async (file: RcFile) => {
    const requestData = new FormData();
    requestData.append('file', file);
    requestData.append('article_id', articleId.toString());

    await uploadFile(requestData);
  };

  const breadcrumbs = useMemo(() => {
    return (
      article?.breadcrumbs?.map((breadcrumb) => ({
        key: breadcrumb.id,
        title: breadcrumb.name,
        onClick: () => navigate(`/knowledge-base/categories/${breadcrumb.id}`),
      })) ?? []
    );
  }, [article, navigate]);

  const navigateBack = () => {
    navigate('/knowledge-base');
  };

  const renderContent = () => {
    if (!isFetchingArticle && !article) {
      return renderNoContent();
    } else {
      return renderArticle();
    }
  };

  const renderNoContent = () => {
    return (
      <Result
        status="404"
        title={t('knowledge_base.articles.detail_page.not_found')}
        extra={
          <Button type="primary" onClick={navigateBack}>
            {t('back')}
          </Button>
        }
      />
    );
  };

  const renderArticle = () => {
    return (
      <Skeleton loading={isFetchingArticle} active paragraph={{ rows: 12 }}>
        <Row>
          <Col xs={24}>
            <Space direction="vertical" size={2}>
              <Breadcrumb
                style={{ cursor: 'pointer' }}
                items={[
                  {
                    title: t('knowledge_base.title'),
                    onClick: navigateBack,
                  },
                  ...breadcrumbs,
                ]}
              />
              <Typography.Text strong style={{ fontSize: 36 }}>
                {article?.title}
              </Typography.Text>
              <Space>
                {article?.user && (
                  <UserAvatar user={article?.user} size="small" />
                )}
                <Typography.Text>
                  {t('knowledge_base.articles.detail_page.created_by', {
                    name: fullUserName(article?.user),
                    date: formatDateTime(article?.created_at ?? ''),
                  })}
                </Typography.Text>
              </Space>
              {userIsAdmin && (
                <Button.Group style={{ marginTop: 20 }}>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() =>
                      navigate(`/knowledge-base/articles/${article?.id}/edit`)
                    }
                  />
                </Button.Group>
              )}
            </Space>
            <Divider />
            <Typography.Paragraph>{HtmlParser(data)}</Typography.Paragraph>
            <Divider />
            <Typography.Title level={5}>
              {t('knowledge_base.articles.detail_page.attachments.title')}
            </Typography.Title>
            <Space size={16}>
              {article?.attachments?.map((attachment) => (
                <KnowledgeBaseAttachmentComponent
                  key={attachment.id}
                  attachment={attachment}
                />
              )) ?? []}
            </Space>
            {userIsAdmin && (
              <Upload.Dragger
                name="files"
                height={150}
                multiple={false}
                accept={ACCEPTED_FILE_TYPES.map((type) => `.${type}`).join(',')}
                beforeUpload={beforeUpload}
                showUploadList={false}
                style={{ marginTop: 20 }}
                disabled={isUploadingFile}
              >
                <Space direction="vertical" size={2}>
                  {isUploadingFile ? (
                    <LoadingOutlined style={{ fontSize: 20 }} />
                  ) : (
                    <UploadOutlined style={{ fontSize: 20 }} />
                  )}
                  <Typography.Text strong>
                    {t(
                      'knowledge_base.articles.detail_page.attachments.upload.title'
                    )}
                  </Typography.Text>
                  <Typography.Text style={{ textOverflow: 'ellipsis' }}>
                    {t(
                      'knowledge_base.articles.detail_page.attachments.upload.description',
                      { types: ACCEPTED_FILE_TYPES.join(', ') }
                    )}
                  </Typography.Text>
                </Space>
              </Upload.Dragger>
            )}
          </Col>
        </Row>
      </Skeleton>
    );
  };

  return <KnowledgeBaseLayout>{renderContent()}</KnowledgeBaseLayout>;
}
